//create a hook that handles comment mutations and returns a loading state
import { useState } from "react";
import { useMutation } from "@apollo/client/react";
import metrics from "~/utils/metrics";
import type {
  PdfHighlightInput,
  CreateAnnotationHighlightMutation,
  CreateAnnotationHighlightMutationVariables,
  CreateHtmlCommentMutation,
  CreateHtmlCommentMutationVariables,
  CreatePdfCommentMutation,
  CreatePdfCommentMutationVariables,
  DeleteCommentMutation,
  DeleteCommentMutationVariables,
} from "~/graphql/codegen/graphql";
import {
  CREATE_ANNOTATION_HIGHLIGHT_MUTATION,
  CREATE_HTML_COMMENT_MUTATION,
  CREATE_PDF_COMMENT_MUTATION,
  DELETE_COMMENT_MUTATION,
} from "../annotate-mutations";
import {
  IHighlightCommon,
  IHtmlHighlightInput,
  IPdfHighlightInput,
} from "~/typings/annotate/annotation";
import useAuthCheck from "~/hooks/check-authenticated";
import { toast } from "@mindstonehq/ui";
import { useLingui } from "@lingui/react/macro";
import { msg } from "@lingui/core/macro";

const useCommentMutations = () => {
  const { t } = useLingui();
  const [isLoading, setIsLoading] = useState(false);
  const checkAuth = useAuthCheck();
  const [deleteCommentMutation] = useMutation<DeleteCommentMutation, DeleteCommentMutationVariables>(DELETE_COMMENT_MUTATION);
  const [createPDFCommentMutation] = useMutation<CreatePdfCommentMutation, CreatePdfCommentMutationVariables>(CREATE_PDF_COMMENT_MUTATION);
  const [createHTMLCommentMutation] = useMutation<CreateHtmlCommentMutation, CreateHtmlCommentMutationVariables>(CREATE_HTML_COMMENT_MUTATION);
  const [createHighlightMutation] = useMutation<CreateAnnotationHighlightMutation, CreateAnnotationHighlightMutationVariables>(CREATE_ANNOTATION_HIGHLIGHT_MUTATION);

  function deleteComment(commentID: string, articleId: string) {
    metrics.track("comment_remove", {
      article_id: articleId,
      view: "annotate",
    });
    return deleteCommentMutation({
      variables: {
        articleId,
        id: commentID,
      },
    }).catch((error) => {
      console.error(error);
      toast.error(t(msg`We couldn't remove this comment. Please try again later.`));
      throw error;
    });
  }

  function addComment(
    content: string,
    selectedHighlight: IHighlightCommon,
    articleID: string,
  ): Promise<{ highlight: IHighlightCommon; commentID: string }> {
    const authenticated = checkAuth(
      t(msg`You need to be logged in to add comments`),
      `${location.href}`,
      "comment_add",
    );
    if (!authenticated) {
      return Promise.resolve({ commentID: "", highlight: selectedHighlight });
    }
    const { comments, canEdit, profile, createdAt, ...inputFields } =
      selectedHighlight as IHighlightCommon;
    const highlight = inputFields as
      | IHtmlHighlightInput
      | IPdfHighlightInput
      | IHighlightCommon;
    metrics.track("comment_create", {
      article_id: articleID,
      view: "annotate",
    });
    if (!highlight.id) {
      console.error("highlight ID is not defined");
      return Promise.reject("highlight ID not defined");
    }
    let promise = Promise.resolve(highlight as IHighlightCommon);
    let xfdfString = "";
    if (highlight.id === "temp") {
      // We need to create the real highlight first. Remove the tempID for saving new
      const { id, ...rest } = highlight;
      if (highlight.__typename === "PDFHighlight") {
        const h = rest as IPdfHighlightInput;
        xfdfString = h.xfdfString || "";
        promise = createHighlight(h, articleID);
      } else {
        const h = rest as IHtmlHighlightInput;
        promise = createHighlight(h, articleID);
      }
    }
    setIsLoading(true);
    return promise
      .then((highlight) => {
        if (highlight.__typename === "PDFHighlight") {
          return createPDFCommentMutation({
            variables: {
              articleId: articleID,
              highlightId: highlight.id,
              content: content,
              xfdfString: xfdfString,
            },
          }).then((res) => {
            setIsLoading(false);
            if (!res?.data) {
              // eslint-disable-next-line lingui/no-unlocalized-strings -- thrown error message, dev-only
              throw "data missing";
            }
            return {
              commentID: res.data?.createPDFComment?.id || "",
              highlight: highlight,
            };
          });
        } else {
          return createHTMLCommentMutation({
            variables: {
              articleId: articleID,
              highlightId: highlight.id,
              content: content,
            },
          }).then((res) => {
            setIsLoading(false);
            if (!res?.data) {
              // eslint-disable-next-line lingui/no-unlocalized-strings -- thrown error message, dev-only
              throw "data missing";
            }
            return {
              commentID: res.data?.createHTMLComment?.id || "",
              highlight: highlight,
            };
          });
        }
      })
      .catch((error) => {
        console.error(error);
        toast.error(t(msg`We couldn't save this comment. Please try again later.`));
        throw error; // Propagate
      });
  }

  function createHighlight(
    h: IHtmlHighlightInput | IPdfHighlightInput,
    articleId: string,
  ) {
    return createHighlightMutation({
      variables: {
        articleId,
        htmlHighlightInput:
          h.__typename === "HTMLHighlight" ? (h as IHtmlHighlightInput) : null,
        pdfHighlightInput:
          h.__typename === "PDFHighlight" ? (h as PdfHighlightInput) : null,
      },
    }).then((res) => {
      if (!res.data) {
        // eslint-disable-next-line lingui/no-unlocalized-strings -- thrown error message, dev-only
        throw "data missing";
      }
      return res.data.createAnnotationHighlight as IHighlightCommon;
    });
  }

  return { isLoading, addComment, deleteComment };
};

export default useCommentMutations;
